<template>
  <button
    @click="handleClick"
    :disabled="loading"
    :aria-pressed="isStarred"
    :aria-label="ariaLabel"
    :class="[
      'inline-flex items-center gap-2 border-none bg-transparent cursor-pointer transition-all duration-300 relative overflow-visible font-inherit',
      sizeClasses,
      variantClasses,
      {
        'opacity-70 cursor-wait': loading,
        'hover:opacity-80': !canStar && !loading
      }
    ]"
  >
    <!-- Star Icon -->
    <span
      :class="[
        'inline-block transition-all duration-300 leading-none',
        {
          'text-yellow-400 animate-pulse': loading,
          'text-yellow-400': isStarred && !loading,
          'text-gray-400': !isStarred && !loading
        }
      ]"
      :style="isStarred && !loading ? 'filter: drop-shadow(0 0 8px rgba(255, 215, 0, 0.6));' : ''"
    >
      {{ isStarred ? '⭐' : '☆' }}
    </span>

    <!-- Star Count -->
    <span v-if="showCount" :class="['font-semibold', isStarred ? 'text-yellow-400' : 'text-gray-700']">
      {{ displayCount }}
    </span>

    <!-- Particle animation container -->
    <div v-if="showParticles" class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
      <div
        v-for="i in 12"
        :key="i"
        class="absolute w-1 h-1 bg-yellow-400 rounded-full animate-particle-explosion"
        :style="getParticleStyle(i)"
      ></div>
    </div>
  </button>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'StarButton',

  props: {
    itemId: {
      type: String,
      required: true
    },
    itemType: {
      type: String,
      required: true,
      validator: (value) => ['place', 'article'].includes(value)
    },
    itemData: {
      type: Object,
      default: () => ({})
    },
    initialStarred: {
      type: Boolean,
      default: false
    },
    initialCount: {
      type: Number,
      default: 0
    },
    showCount: {
      type: Boolean,
      default: true
    },
    size: {
      type: String,
      default: 'medium',
      validator: (value) => ['small', 'medium', 'large'].includes(value)
    },
    variant: {
      type: String,
      default: 'default',
      validator: (value) => ['default', 'compact', 'minimal'].includes(value)
    }
  },

  data() {
    return {
      loading: false,
      showParticles: false,
      localStarred: this.initialStarred,
      localCount: this.initialCount
    };
  },

  computed: {
    ...mapGetters('auth', ['isAuthenticated']),
    ...mapGetters('stars', ['hasUserStarred', 'getStarCount', 'getItemStarData']),

    isStarred() {
      // Use Vuex state if available, otherwise use local state
      const starData = this.getItemStarData(this.itemId, this.itemType);
      return starData ? starData.user_starred : this.localStarred;
    },

    displayCount() {
      // Use Vuex state if available, otherwise use local state
      const starData = this.getItemStarData(this.itemId, this.itemType);
      const count = starData ? starData.star_count : this.localCount;

      // Format large numbers (e.g., 1.2k, 15k)
      if (count >= 1000) {
        return `${(count / 1000).toFixed(1)}k`;
      }
      return count;
    },

    canStar() {
      return this.isAuthenticated;
    },

    sizeClasses() {
      const sizes = {
        small: 'text-sm px-2 py-1 gap-1',
        medium: 'text-base px-3 py-2 gap-2',
        large: 'text-lg px-4 py-3 gap-2.5'
      };
      return sizes[this.size] || sizes.medium;
    },

    variantClasses() {
      const variants = {
        default: 'bg-white/10 border border-yellow-400/30 rounded-full backdrop-blur-sm hover:bg-yellow-400/10 hover:-translate-y-0.5',
        compact: 'px-2 py-1 bg-transparent border-none hover:bg-yellow-400/10 rounded-full',
        minimal: 'px-1 py-0.5 bg-transparent border-none hover:bg-yellow-400/5 rounded'
      };
      return variants[this.variant] || variants.default;
    },

    ariaLabel() {
      if (!this.canStar) {
        return 'Sign in to star this item';
      }
      return this.isStarred
        ? `Remove star from ${this.itemType}`
        : `Star this ${this.itemType}`;
    }
  },

  async mounted() {
    // Fetch star data on mount if authenticated
    if (this.isAuthenticated) {
      try {
        await this.fetchItemStars({
          itemId: this.itemId,
          itemType: this.itemType
        });
      } catch (error) {
        console.error('Error fetching star data:', error);
      }
    }
  },

  methods: {
    ...mapActions('stars', ['toggleStar', 'fetchItemStars']),

    async handleClick() {
      // Prevent action if not authenticated
      if (!this.canStar) {
        this.$emit('auth-required');
        this.showAuthModal();
        return;
      }

      // Prevent double-clicks
      if (this.loading) return;

      this.loading = true;

      try {
        // Toggle star with optimistic update
        const result = await this.toggleStar({
          itemId: this.itemId,
          itemType: this.itemType,
          itemData: this.itemData
        });

        // Show success feedback
        if (result.action === 'starred') {
          this.showStarAnimation();
          this.showSuccessToast(`You starred this ${this.itemType}!`);
        }

        // Emit events
        this.$emit('toggle', {
          starred: result.action === 'starred',
          count: result.starCount
        });

      } catch (error) {
        console.error('Error toggling star:', error);
        this.showErrorToast('Failed to update star. Please try again.');

        // Emit error event
        this.$emit('error', error);
      } finally {
        this.loading = false;
      }
    },

    showStarAnimation() {
      this.showParticles = true;
      setTimeout(() => {
        this.showParticles = false;
      }, 1000);
    },

    getParticleStyle(index) {
      const angle = (360 / 12) * index;
      const distance = 40;
      const x = Math.cos((angle * Math.PI) / 180) * distance;
      const y = Math.sin((angle * Math.PI) / 180) * distance;

      return {
        '--tx': `${x}px`,
        '--ty': `${y}px`,
        animationDelay: `${index * 0.05}s`
      };
    },

    showAuthModal() {
      // Emit event to parent to show auth modal
      this.$emit('auth-required');

      // Or dispatch global event
      window.dispatchEvent(new CustomEvent('show-auth-modal'));
    },

    showSuccessToast(message) {
      // Simple toast implementation
      const toast = document.createElement('div');
      toast.className = 'fixed top-20 right-5 px-5 py-3 rounded-lg bg-white shadow-lg z-[10000] border-l-4 border-green-500 text-green-800 text-sm font-medium animate-slide-in';
      toast.textContent = message;
      document.body.appendChild(toast);

      setTimeout(() => {
        toast.remove();
      }, 3000);
    },

    showErrorToast(message) {
      const toast = document.createElement('div');
      toast.className = 'fixed top-20 right-5 px-5 py-3 rounded-lg bg-white shadow-lg z-[10000] border-l-4 border-red-500 text-red-800 text-sm font-medium animate-slide-in';
      toast.textContent = message;
      document.body.appendChild(toast);

      setTimeout(() => {
        toast.remove();
      }, 3000);
    }
  }
};
</script>

<style scoped>
/* Star pulse animation when starred */
.text-yellow-400:not(.animate-pulse) {
  animation: starPulse 0.5s ease;
}

@keyframes starPulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.3); }
}

/* Particle explosion animation */
@keyframes particle-explosion {
  0% {
    transform: translate(0, 0) scale(1);
    opacity: 1;
  }
  100% {
    transform: translate(var(--tx), var(--ty)) scale(0);
    opacity: 0;
  }
}

.animate-particle-explosion {
  animation: particle-explosion 1s ease-out forwards;
}

/* Slide in animation for toasts */
@keyframes slide-in {
  from {
    transform: translateX(400px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.animate-slide-in {
  animation: slide-in 0.3s ease;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  :global(.fixed.top-20.right-5) {
    right: 10px;
    left: 10px;
    top: 70px;
  }
}
</style>
